import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/store';
import { fetchAdminRows, fetchAdminStats, type AdminRow, type AdminStat } from './adminApi';

type AdminState = {
  rows: AdminRow[];
  loading: boolean;
  offset: number;
  limit: number;
  sortBy: string;
  order: 'asc' | 'desc';
  stats?: AdminStat[];
  error?: string | null;
};

const initialState: AdminState = {
  rows: [],
  loading: false,
  offset: 0,
  limit: 50,
  sortBy: 'createdAt',
  order: 'desc',
  stats: undefined,
  error: null,
};

export const loadAdminStats = createAsyncThunk(
  'admin/loadStats',
  async (_, { getState }) => {
    const state = getState() as RootState;
    const username = state.user.username;
    if (!username) { return [] as AdminStat[]; }
    return await fetchAdminStats(username);
  }
);

export const loadAdminRows = createAsyncThunk(
  "admin/loadRows",
  async (
    args: { offset?: number; limit?: number; sortBy?: string; order?: 'asc' | 'desc' },
    { getState }
  ) => {
    const state = getState() as RootState;
    const username = state.user.username;

    if (!username) { return [] as AdminRow[]; }

    const offset = args.offset ?? state.admin.offset;
    const limit = args.limit ?? state.admin.limit;
    const sortBy = args.sortBy ?? state.admin.sortBy;
    const order = args.order ?? state.admin.order;

    const data = await fetchAdminRows(username, { offset, limit, sortBy, order });
    return { data, offset, limit, sortBy, order };
  }
);

const slice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    setPaging(state, action: PayloadAction<{ offset: number; limit: number }>) {
      state.offset = action.payload.offset;
      state.limit = action.payload.limit;
    },
    setSort(state, action: PayloadAction<{ sortBy: string; order: 'asc' | 'desc' }>) {
      state.sortBy = action.payload.sortBy;
      state.order = action.payload.order;
    },
    clearRows(state) {
      state.rows = [];
      state.offset = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAdminRows.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(loadAdminRows.fulfilled, (s, a) => {
        s.loading = false;
        if (Array.isArray(a.payload)) { s.rows = []; return; }

        s.rows = a.payload.data;
        s.offset = a.payload.offset;
        s.limit = a.payload.limit;
        s.sortBy = a.payload.sortBy;
        s.order = a.payload.order;
      })
      .addCase(loadAdminRows.rejected, (s, a) => {
        s.loading = false;
        s.error = a.error.message ?? 'Failed to load rows';
      })

      .addCase(loadAdminStats.fulfilled, (s, a) => {
        s.stats = a.payload;
      })
      .addCase(loadAdminStats.rejected, (s, a) => {
        s.error = a.error.message ?? 'Failed to load stats';
      });
  },
});

export const { setPaging, setSort, clearRows } = slice.actions;
export default slice.reducer;

export const selectAdmin = (s: RootState) => s.admin;
